<template>
  <div v-if="visibleBanners.length > 0" class="banner-carousel-section">
    <div class="section-title">
      <h3 class="text-h4 text-brand-1 text-bold">conheça nossas novidades!</h3>
    </div>

    <div class="banner-carousel-container">
      <q-carousel
        v-model="currentSlide"
        transition-prev="slide-right"
        transition-next="slide-left"
        swipeable
        animated
        control-color="primary"
        :autoplay="autoplayInterval"
        arrows
        infinite
        class="banner-carousel"
      >
        <q-carousel-slide
          v-for="(banner, index) in visibleBanners"
          :key="banner.id"
          :name="index"
          class="banner-slide"
        >
          <div class="banner-content">
            <q-img
              :src="banner.image"
              :alt="banner.title"
              class="banner-image"
              fit="cover"
              no-spinner
              no-transition
              @error="onImageError"
            >
              <div class="banner-overlay">
                <div class="banner-text">
                  <h4 class="banner-title">{{ banner.title }}</h4>
                  <p v-if="banner.description" class="banner-description">
                    {{ banner.description }}
                  </p>
                  <q-btn
                    v-if="banner.buttonText && banner.buttonAction"
                    :label="banner.buttonText"
                    color="primary"
                    @click="handleButtonClick(banner)"
                    class="banner-button"
                  />
                </div>
              </div>
            </q-img>

            <!-- Botão de fechar -->
            <q-btn
              icon="close"
              flat
              round
              dense
              color="white"
              class="banner-close-btn"
              @click="closeBanner(banner)"
              :title="`Fechar banner: ${banner.title}`"
            />
          </div>
        </q-carousel-slide>
      </q-carousel>

      <!-- Indicadores customizados (se houver mais de um banner) -->
      <div v-if="visibleBanners.length > 1" class="banner-indicators">
        <q-btn
          v-for="(banner, index) in visibleBanners"
          :key="`indicator-${banner.id}`"
          :class="['indicator-dot', { active: currentSlide === index }]"
          @click="currentSlide = index"
          round
          dense
          size="8px"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from "vue";
import { bannerService } from "../services/bannerService";

export default {
  name: "BannerCarousel",
  props: {
    cliente: {
      type: [Object, String],
      default: () => ({}),
    },
  },
  emits: ["banner-closed", "banner-action"],
  setup(props, { emit }) {
    const currentSlide = ref(0);
    const autoplayInterval = ref(5000); // 5 segundos
    const banners = ref([]);
    const visibleBanners = ref([]);

    // Carregar banners baseado no cliente
    const loadBanners = async () => {
      try {
        // Suporte para cliente como string ou objeto
        let clienteTenant = "default";

        if (typeof props.cliente === "string") {
          clienteTenant = props.cliente;
        } else if (props.cliente && typeof props.cliente === "object") {
          clienteTenant =
            props.cliente.identificador || props.cliente.nome || "default";
        }

        banners.value = await bannerService.getBannersForClient(clienteTenant);

        updateVisibleBanners();
      } catch (error) {
        console.error("Erro ao carregar banners:", error);
        banners.value = [];
        visibleBanners.value = [];
      }
    };

    // Atualizar banners visíveis (não fechados)
    const updateVisibleBanners = () => {
      visibleBanners.value = banners.value.filter((banner) => {
        const isClosed = bannerService.isBannerClosed(
          banner.id,
          banner.version
        );
        return !isClosed;
      });
    };

    // Fechar um banner específico
    const closeBanner = (banner) => {
      bannerService.closeBanner(banner.id, banner.version);
      updateVisibleBanners();

      // Ajustar slide atual se necessário
      if (
        currentSlide.value >= visibleBanners.value.length &&
        visibleBanners.value.length > 0
      ) {
        currentSlide.value = visibleBanners.value.length - 1;
      }

      emit("banner-closed", banner);
    };

    // Manipular clique no botão do banner
    const handleButtonClick = (banner) => {
      if (banner.buttonAction) {
        if (banner.buttonAction.type === "url" && banner.buttonAction.value) {
          window.open(banner.buttonAction.value, "_blank");
        } else if (
          banner.buttonAction.type === "route" &&
          banner.buttonAction.value
        ) {
          // Emitir evento para o componente pai lidar com navegação
          emit("banner-action", {
            type: "navigate",
            route: banner.buttonAction.value,
            banner: banner,
          });
        } else if (
          banner.buttonAction.type === "function" &&
          banner.buttonAction.value
        ) {
          // Emitir evento para função customizada
          emit("banner-action", {
            type: "function",
            function: banner.buttonAction.value,
            banner: banner,
          });
        }
      }
    };

    // Manipular erro de carregamento de imagem
    const onImageError = (error) => {
      console.warn("Erro ao carregar imagem do banner:", error);
    };

    // Computed para verificar se deve mostrar o carrossel
    const shouldShowCarousel = computed(() => {
      return visibleBanners.value.length > 0;
    });

    // Lifecycle
    onMounted(() => {
      loadBanners();
    });

    // Watch para detectar mudanças no cliente (se necessário recarregar)
    watch(
      () => props.cliente,
      () => {
        loadBanners();
      },
      { deep: true }
    );

    return {
      currentSlide,
      autoplayInterval,
      banners,
      visibleBanners,
      shouldShowCarousel,
      closeBanner,
      handleButtonClick,
      onImageError,
    };
  },
};
</script>

<style scoped>
.banner-carousel-section {
  margin-bottom: 2rem;
  width: 100%;
}

.section-title {
  text-align: center;
  margin-bottom: 1rem;
}

.section-title h3 {
  margin: 0;
  color: var(--q-primary);
}

.banner-carousel-container {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.banner-carousel {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
}

.banner-slide {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
}

.banner-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border: none;
  outline: none;
}

.banner-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 2rem;
  color: white;
}

.banner-text {
  max-width: 70%;
}

.banner-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 0 0.5rem 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.banner-description {
  font-size: 1rem;
  margin: 0 0 1rem 0;
  line-height: 1.4;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

.banner-button {
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.banner-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;
}

.banner-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.banner-indicators {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 8px;
  z-index: 10;
}

.indicator-dot {
  width: 12px !important;
  height: 12px !important;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5) !important;
  border: none !important;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 12px !important;
  min-height: 12px !important;
}

.indicator-dot.active {
  background-color: rgba(255, 255, 255, 0.9) !important;
  transform: scale(1.2);
}

.indicator-dot:hover {
  background-color: rgba(255, 255, 255, 0.8) !important;
}

/* Responsividade */
@media (max-width: 768px) {
  .banner-carousel {
    height: 250px;
  }

  .banner-overlay {
    padding: 1rem;
  }

  .banner-text {
    max-width: 80%;
  }

  .banner-title {
    font-size: 1.2rem;
  }

  .banner-description {
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .banner-carousel {
    height: 200px;
  }

  .banner-text {
    max-width: 85%;
  }

  .banner-title {
    font-size: 1.1rem;
  }

  .banner-description {
    font-size: 0.8rem;
  }

  .banner-close-btn {
    top: 5px;
    right: 5px;
  }
}

/* Customização do Quasar Carousel */
:deep(.q-carousel__control) {
  display: none !important; /* Remove todos os controles */
}

:deep(.q-carousel__arrow) {
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--q-primary);
}

:deep(.q-carousel__arrow:hover) {
  background-color: white;
}

:deep(.q-carousel__thumbnails) {
  display: none !important; /* Remove thumbnails */
}

:deep(.q-carousel__slides-container) {
  height: 100%;
  background: transparent;
}

:deep(.q-carousel__slide) {
  background: transparent;
  padding: 0;
  margin: 0;
}

:deep(.q-img) {
  background: transparent;
}

:deep(.q-img__container) {
  background: transparent;
}

:deep(.q-carousel__navigation) {
  display: none !important; /* Remove navegação inferior */
}

:deep(.q-carousel__track) {
  background: transparent;
}

:deep(.q-carousel__prev),
:deep(.q-carousel__next) {
  background: rgba(255, 255, 255, 0.8);
  color: var(--q-primary);
  border-radius: 50%;
}

:deep(.q-carousel__prev:hover),
:deep(.q-carousel__next:hover) {
  background: rgba(255, 255, 255, 0.95);
}
</style>
